// Minimal, bagimlilik-free markdown -> JSX. Destek: **kalin**, *italik*, `kod`,
// [metin](url) linkler, > alinti, -/*/+ ve 1. listeler, #..###### basliklar,
// --- yatay cizgi, GFM pipe tablolari, ``` kod bloklari, paragraf + satir sonu.
// Emoji dogal gecer. raw HTML kullanmaz (dangerouslySetInnerHTML yok) — XSS'e kapalidir.

/** Satir-ici bicimleme: **kalin**, *italik*, `kod`, [metin](url). */
function inline(text: string): (string | JSX.Element)[] {
  const nodes: (string | JSX.Element)[] = [];
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`|\[([^\]]+)\]\((https?:\/\/[^)\s]+)\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[2] !== undefined) nodes.push(<strong key={k++}>{m[2]}</strong>);
    else if (m[3] !== undefined) nodes.push(<em key={k++}>{m[3]}</em>);
    else if (m[4] !== undefined) nodes.push(<code key={k++}>{m[4]}</code>);
    else if (m[5] !== undefined) nodes.push(<a key={k++} href={m[6]} target="_blank" rel="noopener noreferrer">{m[5]}</a>);
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/** Paragraf icindeki tek satir sonlarini <br/> yapar. */
function withBreaks(text: string): (string | JSX.Element)[] {
  const out: (string | JSX.Element)[] = [];
  text.split("\n").forEach((ln, i) => {
    if (i > 0) out.push(<br key={`br${i}`} />);
    out.push(...inline(ln));
  });
  return out;
}

/** GFM tablo satirini hucrelere bol (bas/son | kirpilir). */
function splitRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|")) s = s.slice(0, -1);
  return s.split("|").map((c) => c.trim());
}
/** Ayrac satiri mi: |---|:--:|---| gibi. */
const isTableSep = (l: string) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(l);

export default function Markdown({ text }: { text: string }) {
  const lines = (text ?? "").replace(/\r\n/g, "\n").replace(/\t/g, "  ").split("\n");
  const out: JSX.Element[] = [];
  let key = 0;
  let para: string[] = [];

  // Biriken paragraf grubunu siniflandirip (baslik/hr/alinti/liste/paragraf) ekle.
  const flush = () => {
    const block = para.join("\n").trim();
    para = [];
    if (!block) return;
    const bl = block.split("\n");

    const h = /^(#{1,6})\s+(.*)$/.exec(bl[0]);
    if (bl.length === 1 && h) {
      const lvl = Math.min(h[1].length + 2, 6);
      const Tag = `h${lvl}` as keyof JSX.IntrinsicElements;
      out.push(<Tag key={key++}>{inline(h[2])}</Tag>);
      return;
    }
    if (bl.length === 1 && /^([-*_])\1{2,}$/.test(bl[0].replace(/\s/g, ""))) {
      out.push(<hr key={key++} />);
      return;
    }
    if (bl.every((l) => /^>\s?/.test(l))) {
      const inner = bl.map((l) => l.replace(/^>\s?/, "")).join("\n");
      out.push(<blockquote key={key++}>{withBreaks(inner)}</blockquote>);
      return;
    }
    if (bl.every((l) => /^\s*[-*+]\s+/.test(l))) {
      out.push(<ul key={key++}>{bl.map((l, li) => <li key={li}>{inline(l.replace(/^\s*[-*+]\s+/, ""))}</li>)}</ul>);
      return;
    }
    if (bl.every((l) => /^\s*\d+\.\s+/.test(l))) {
      out.push(<ol key={key++}>{bl.map((l, li) => <li key={li}>{inline(l.replace(/^\s*\d+\.\s+/, ""))}</li>)}</ol>);
      return;
    }
    out.push(<p key={key++}>{withBreaks(block)}</p>);
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // ``` kod bloku
    if (/^\s*```/.test(line)) {
      flush();
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i])) { code.push(lines[i]); i++; }
      i++; // kapanis ```
      out.push(<pre key={key++}><code>{code.join("\n")}</code></pre>);
      continue;
    }

    // GFM tablo: bu satirda | var ve sonraki satir ayrac
    if (line.includes("|") && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      flush();
      const header = splitRow(line);
      i += 2; // baslik + ayrac
      const rows: string[][] = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim() !== "") {
        rows.push(splitRow(lines[i]));
        i++;
      }
      out.push(
        <div className="md-table-wrap" key={key++}>
          <table className="md-table">
            <thead><tr>{header.map((c, ci) => <th key={ci}>{inline(c)}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>{header.map((_, ci) => <td key={ci}>{inline(r[ci] ?? "")}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    if (line.trim() === "") { flush(); i++; continue; }
    para.push(line);
    i++;
  }
  flush();

  return <div className="md">{out}</div>;
}
